import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styles from './UniversityBadge.module.scss';
import { getUniversityPalette } from './universityPalettes';

/** `compact` tightens the chip's padding for dense rows. */
export type UniversityBadgeSize = 'regular' | 'compact';
export interface UniversityBadgeProps {
  /**
   * The university as the page names it: the chip's text and its palette key
   * (matched exactly, then ignoring case). A name the palette does not hold
   * renders the brand chip.
   */
  name: string;
  size?: UniversityBadgeSize;
}

// The two component-internal properties the palette pair travels in. They
// are not `--helios-*` (only the token generator writes those) and nothing
// outside this component reads them.
type PaletteStyle = CSSProperties & {
  '--university-badge-background': string;
  '--university-badge-text': string;
};

/**
 * The university chip (Phase 5 PR 5.0, incubating; plan §7 row 10): the
 * redesign's skewed highlight (`UniversityHighlight`) on tokens, coloured
 * from the Helios palette data (`universityPalettes.ts`). An unknown name is
 * the brand chip (the surface's action colours), never plain text; a blank
 * name renders nothing. The pair reaches the chip as two internal custom
 * properties set inline; there is no public `style` or `className`.
 */
function UniversityBadge(t0) {
  const $ = _c(11);
  const {
    name,
    size: t1
  } = t0;
  const size = t1 === undefined ? "regular" : t1;
  if (!name.trim()) {
    return null;
  }
  let t2;
  if ($[0] !== name) {
    t2 = getUniversityPalette(name);
    $[0] = name;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const palette = t2;
  let t3;
  if ($[2] !== palette) {
    t3 = palette ? {
      "--university-badge-background": palette.background,
      "--university-badge-text": palette.color
    } : undefined;
    $[2] = palette;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const style = t3;
  const t4 = size === "compact" && styles["helios-university-badge--compact"];
  let t5;
  if ($[4] !== t4) {
    t5 = classNames(styles["helios-university-badge"], t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = palette ? "university" : "default";
  let t7;
  if ($[6] !== name || $[7] !== style || $[8] !== t5 || $[9] !== t6) {
    t7 = <span className={t5} data-helios-university-badge="" data-palette={t6} style={style}>{name}</span>;
    $[6] = name;
    $[7] = style;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
export default UniversityBadge;